// src/app/core/services/authentication.service.ts

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Auth } from "aws-amplify";
import { BehaviorSubject, Observable, from, firstValueFrom } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";

// DTOs
export interface CognitoUserDto {
  Username: string;
  Attributes: { Name: string; Value: string }[];
  UserCreateDate: string;
  UserLastModifiedDate: string;
  Enabled: boolean;
  UserStatus: string;
}

export interface PasswordChallengeDto {
  challengeName: string;
  session: string;
  username: string;
  name: string;
}

export interface UserDto {
  userId: string;
  name: string;
  email: string;
  email_verified: boolean;
  phone_number?: string;
  phone_number_verified?: boolean;
  groups: string[];
}

export interface ChallengeAnswerInput {
  username: string;
  session: string;
  newPassword: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  private $cgtUsers = new BehaviorSubject<CognitoUserDto[]>([]);
  public $idToken = new BehaviorSubject<string | undefined>(undefined);
  public $user = new BehaviorSubject<UserDto | undefined>(undefined);
  public $isAuthenticated = new BehaviorSubject<boolean>(false);
  public $isLoading = new BehaviorSubject<boolean>(true);
  public $passwordChallenge = new BehaviorSubject<
    PasswordChallengeDto | undefined
  >(undefined);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {
    this.autoLogin();
  }

  async autoLogin(): Promise<void> {
    try {
      const session = (await Auth.currentSession()).getIdToken();

      const user: UserDto = {
        userId: session.payload["custom:system_id"],
        name: session.payload["name"],
        email: session.payload["email"],
        email_verified: session.payload["email_verified"],
        phone_number: session.payload["phone_number"],
        phone_number_verified: session.payload["phone_number_verified"],
        groups: session.payload["cognito:groups"] || [],
      };

      this.checkGroups(user.groups);

      this.$idToken.next(session.getJwtToken());
      this.$user.next(user);
      this.$isAuthenticated.next(true);

      this.$isLoading.next(false);
    } catch {
      this.$isLoading.next(false);
    }
  }

  public get $cognitoUsers(): BehaviorSubject<CognitoUserDto[]> {
    let count = 0;
    const sub = this.$cgtUsers.subscribe({
      next: (users: CognitoUserDto[]) => {
        count = users.length;
      },
    });
    sub.unsubscribe();
    if (count < 1) {
      this.http
        .get<CognitoUserDto[]>(`${environment.restApi}/auth/cognito-users`)
        .subscribe({
          next: (users: CognitoUserDto[]) => {
            this.$cgtUsers.next(users);
          },
        });
    }
    return this.$cgtUsers;
  }

  /**
   * SIGN_IN A function to make sign-in in clina service
   * @param username user email
   * @param password user password
   */
  signIn(
    username: string,
    password: string,
  ): Observable<PasswordChallengeDto | void> {
    return from(this._signIn(username, password));
  }

  private async _signIn(
    username: string,
    password: string,
  ): Promise<PasswordChallengeDto | void> {
    try {
      const user = await Auth.signIn(username, password);

      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        const challenge: PasswordChallengeDto = {
          challengeName: user.challengeName,
          session: user.Session,
          username: user.username,
          name: user.challengeParam.userAttributes.name,
        };
        this.$passwordChallenge.next(challenge);
        return challenge;
      }

      if (user.challengeName) {
        // Qualquer outro desafio (SMS_MFA, SOFTWARE_TOKEN_MFA, MFA_SETUP...) não é
        // suportado por esta aplicação e não deve virar "erro no sistema".
        console.error("Sign-in: desafio não suportado:", user.challengeName);
        return Promise.reject(
          `Não foi possível concluir o login: desafio "${user.challengeName}" não suportado.`,
        );
      }

      const userSession = user?.getSignInUserSession?.();
      if (!userSession) {
        console.error("Sign-in: Auth.signIn retornou sem sessão.", user);
        return Promise.reject(
          "Não foi possível obter a sessão do usuário. Tente novamente.",
        );
      }

      const session = userSession.getIdToken();
      const token = session.getJwtToken();

      const u: UserDto = {
        userId: session.payload["custom:system_id"],
        name: session.payload["name"],
        email: session.payload["email"],
        email_verified: session.payload["email_verified"],
        phone_number: session.payload["phone_number"],
        phone_number_verified: session.payload["phone_number_verified"],
        groups: session.payload["cognito:groups"] || [],
      };

      this.checkGroups(u.groups);

      this.$idToken.next(token);
      this.$user.next(u);
      this.$isAuthenticated.next(true);
    } catch (error: any) {
      // Sem este log a causa real fica invisível e todo erro não mapeado vira
      // a mensagem genérica "Erro no sistema".
      //
      // Em produção só os campos identificados: o erro cru do provedor de
      // autenticação pode carregar dados da requisição, e o console é lido por
      // extensões do navegador e por qualquer coleta de telemetria.
      console.error("Sign-in Error:", {
        code: error?.code,
        name: error?.name,
        message: error?.message,
        ...(environment.production ? {} : { error }),
      });

      const kind = error?.code ?? error?.name ?? "";
      const raw = String(error?.message ?? error);

      const matches = (needle: string) =>
        kind === needle || raw.includes(needle);

      // Submit concorrente: existe outro Auth.signIn legítimo em andamento.
      // Deslogar aqui derrubaria a sessão que a outra chamada está criando.
      if (matches("Pending sign-in attempt already in progress")) {
        return Promise.reject("Autenticação em andamento, aguarde.");
      }

      try {
        await Auth.signOut(); // Force clear session
      } catch (signOutError) {
        console.error("Sign-out Error (ignored):", signOutError);
      }

      if (matches("NotAuthorizedException")) {
        return Promise.reject("Usuário ou senha incorretos!");
      }
      if (matches("UserNotFoundException")) {
        return Promise.reject("Usuário ou senha incorretos!");
      }
      if (matches("UserNotConfirmedException")) {
        return Promise.reject("É necessário confirmar o e-mail cadastrado!");
      }
      if (matches("PasswordResetRequiredException")) {
        return Promise.reject(
          "É necessário redefinir sua senha. Use a opção 'Esqueceu a senha?'.",
        );
      }
      if (matches("TooManyRequestsException") || matches("LimitExceededException")) {
        return Promise.reject(
          "Muitas tentativas em sequência. Aguarde alguns minutos e tente novamente.",
        );
      }
      if (matches("UserHasNotAllowGroup")) {
        return Promise.reject(
          "Você não possui permissão para acessar esta aplicação.",
        );
      }
      if (matches("NetworkError") || matches("Network error")) {
        return Promise.reject(
          "Falha de conexão com o servidor de autenticação. Verifique sua internet e tente novamente.",
        );
      }

      // O código do provedor fica no console, não na tela: para quem está
      // tentando entrar ele não significa nada e não muda o que fazer.
      return Promise.reject("Erro no sistema, entre em contato com a Clina.");
    } finally {
      this.$isLoading.next(false);
    }
  }

  async signOut() {
    try {
      await Auth.signOut();
      this.$isAuthenticated.next(false);
      this.$idToken.next(undefined);
      this.router.navigate(["/auth/login"]);
      this.$user.next(undefined);
    } catch (error: any) {
      throw new Error(error);
    }
  }

  /**
   * FORGOT_PASSWORD
   * a method to send a code to user email (must be used with CONFGIRM_FORGOT_PASSWORD)
   * @param email user email
   */
  forgotPassword(email: string): Observable<any> {
    return from(Auth.forgotPassword(email));
  }

  /**
   * CONFGIRM_FORGOT_PASSWORD
   * A method to confirm a forgot password by a verification code send to email
   * @param code
   * @param email
   * @param new_password
   * @returns
   */
  confirmForgotPassword(
    code: string,
    email: string,
    new_password: string,
  ): Observable<any> {
    return from(Auth.forgotPasswordSubmit(email, code, new_password)).pipe(
      catchError((_err: any) => {
        throw new Error("INVALID_CODE");
      }),
    );
  }

  checkGroups(groups: string[]) {
    const canAccess =
      groups.includes("admin") ||
      groups.includes("BKF_ADMIN") ||
      groups.includes("MANAGER") ||
      groups.includes("CONSULTANT");
    if (!canAccess) {
      throw Error("UserHasNotAllowGroup");
    }
  }

  changeTemporaryPassword(input: ChallengeAnswerInput): Promise<any> {
    return firstValueFrom(
      this.http.post(`${environment.restApiMain}/auth/respondChallenge`, input),
    );
  }
}
